import { useEffect } from "react";
import NextImage from "next/image";
import { Box, Heading, Stack, Text } from "@chakra-ui/react";

import Button from "components/Chakra/Button";
import CustomLink, { ILinkProps } from "components/Chakra/CustomLink";
import useGlobalState from "hooks/useGlobalState";
import {
  useModal,
  useCreateModal,
  InitialUseCreateModalType,
} from "../ModalsProvider";
import useRouter from "hooks/useRouter";
import { isSearchPath } from "helpers/search";
import { TrackEventProps, useTracking } from "contexts/Tracking";
import { useFlagsmith } from "contexts/Flagsmith";

type VariantObjectProps = {
  title: string;
  text: string;
  to: ILinkProps["to"];
  track: TrackEventProps;
  linkText: string;
};

const getCurrentOption = (currentPath: string, variant?: string) => {
  const variants: {
    one: VariantObjectProps;
    two: VariantObjectProps;
  } = {
    one: {
      title: "Vystupte z řady zájemců",
      text: "Zvyšte šanci, že si majitel vybere právě vás. Vylepšete si profil.",
      to: "/nastaveni",
      track: {
        "dl.eCat": currentPath,
        "dl.eAct": "bttn.clicked",
        "dl.eLab": "Profil",
      },
      linkText: "Zlepšit můj účet",
    },
    two: {
      title: "Vynikněte mezi ostatními",
      text: "Jako zájemce s prověřením získáte výhodu v očích majitelů.",
      to: "/sebeprovereni",
      track: {
        "dl.eCat": currentPath,
        "dl.eAct": "scc.clicked",
        "dl.eLab": "Prověřit se",
      },
      linkText: "Prověřit údaje",
    },
  };
  switch (variant) {
    case "original_bttn_profil":
      return variants.one;
    case "bttn_scc_na_success":
      return variants.two;
    default:
      return variants.one;
  }
};

const MessageSentModal: React.FC = () => {
  const { closeAllModals } = useModal("messageSentModal");
  const { user } = useGlobalState();

  const { asPath } = useRouter();
  const { getFlagByName } = useFlagsmith();

  const path = isSearchPath(asPath) ? "search" : "offer_detail";
  const flag = getFlagByName("scc_after_contact");

  const { trackEvent } = useTracking();

  const currentOption = getCurrentOption(
    path,
    flag?.enabled ? flag.value?.toString() : undefined,
  );

  useEffect(() => {
    if (flag?.enabled && flag.value) {
      trackEvent({
        "dl.eCat": path,
        "dl.eAct": "ab.participated",
        "dl.eLab": flag.value.toString(),
      });
    }
  }, [flag?.value, path]);

  return (
    <Box p={[8, 12]} textAlign="center" backgroundColor="ud-grey-100">
      <Stack align="center" mb="10px">
        <NextImage
          src="/img/common/message_sent.png"
          alt="Obrázek odeslané zprávy"
          width="80"
          height="80"
        />
      </Stack>
      <Heading as="h2" color="ud-black" textAlign="center" mb="30px">
        Zpráva odeslána
      </Heading>
      {user?.score !== 100 && !user?.premiumTill ? (
        <Stack
          borderRadius="6px"
          borderWidth="1px"
          borderColor="ud-grey-300"
          backgroundColor="ud-white"
          m="auto"
          py="30px"
          px="20px"
          spacing="0px"
          align="center"
        >
          <NextImage
            src="/img/icons/exekuce.svg"
            alt="Ilustrační obrázek zájemce"
            width="80"
            height="80"
          />
          <Heading as="h3" variant="h3_1" pt="20px" pb="8px" textAlign="center">
            {currentOption.title}
          </Heading>
          <Text textAlign="center" variant="small_2" pb="30px">
            {currentOption.text}
          </Text>
          <CustomLink
            to={currentOption.to}
            trackEventPayload={currentOption.track}
            onClick={closeAllModals}
            variant="button"
          >
            {currentOption.linkText}
          </CustomLink>
        </Stack>
      ) : (
        <Stack align="center">
          <Button onClick={closeAllModals}>Rozumím</Button>
        </Stack>
      )}
    </Box>
  );
};

export const useMessageSentModal: InitialUseCreateModalType = () =>
  useCreateModal("messageSentModal", MessageSentModal, null, null, {
    size: "sm",
    variant: "noPadding",
  });
